import React, { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
    FiMail, FiBellOff, FiBell, FiAlertCircle, FiArrowLeft,
} from 'react-icons/fi';
import {
    checkUnsubscribeApi, unsubscribeEmailApi, resubscribeEmailApi,
} from '../../Api';
import { notify } from '../../components/Notify';

// Estados:
//   'checking' -> consultando o estado atual
//   'invalid'  -> link assinado errado / uid faltando
//   'subscribed' -> está recebendo emails (mostra botão pra cancelar)
//   'unsubscribed' -> não recebe (mostra botão pra voltar a receber)
export default function DescadastrarEmail() {
    const [params] = useSearchParams();
    const uid = params.get('uid') || '';
    const sig = params.get('sig') || '';

    const [status, setStatus] = useState('checking');
    const [email, setEmail] = useState('');
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!uid || !sig) {
            setStatus('invalid');
            return;
        }
        let cancelado = false;
        (async () => {
            try {
                const res = await checkUnsubscribeApi({ uid, sig });
                if (cancelado) return;
                if (!res.data?.valid) {
                    setStatus('invalid');
                    return;
                }
                setEmail(res.data.email || '');
                setStatus(res.data.unsubscribed ? 'unsubscribed' : 'subscribed');
            } catch {
                if (!cancelado) setStatus('invalid');
            }
        })();
        return () => { cancelado = true; };
    }, [uid, sig]);

    async function handleToggle() {
        if (loading) return;
        setLoading(true);
        try {
            if (status === 'subscribed') {
                await unsubscribeEmailApi({ uid, sig });
                setStatus('unsubscribed');
                notify('Você não receberá mais emails da UEN.', 'success');
            } else if (status === 'unsubscribed') {
                await resubscribeEmailApi({ uid, sig });
                setStatus('subscribed');
                notify('Você voltou a receber emails da UEN.', 'success');
            }
        } catch (err) {
            notify(err.response?.data?.error || 'Não foi possível atualizar a preferência.', 'error');
        } finally {
            setLoading(false);
        }
    }

    return (
        <div style={styles.page}>
            <div style={styles.card}>
                <div style={styles.brand}>
                    <div style={styles.brandLogo}>U</div>
                    <div style={styles.brandText}>
                        <span style={styles.brandName}>UEN</span>
                        <span style={styles.brandTag}>Estudantil</span>
                    </div>
                </div>

                {status === 'checking' && (
                    <div style={styles.centerBox}>
                        <span style={{ ...styles.spinner, borderTopColor: '#059669', borderColor: 'rgba(5,150,105,0.2)' }} />
                        <p style={styles.subtitle}>Carregando…</p>
                    </div>
                )}

                {status === 'invalid' && (
                    <div style={styles.centerBox}>
                        <div style={{ ...styles.iconCircle, background: '#FEF2F2', color: '#DC2626' }}>
                            <FiAlertCircle size={36} />
                        </div>
                        <h1 style={styles.title}>Link inválido</h1>
                        <p style={styles.subtitle}>
                            Este link de cancelamento de inscrição é inválido ou está incompleto.
                            Se você precisa parar de receber emails, acesse seu painel UEN
                            ou entre em contato com a equipe.
                        </p>
                        <Link to="/login" style={{ ...styles.backLink, marginTop: 0 }}>
                            <FiArrowLeft size={14} /> Ir para o login
                        </Link>
                    </div>
                )}

                {status === 'subscribed' && (
                    <div style={styles.centerBox}>
                        <div style={{ ...styles.iconCircle, background: '#ECFDF5', color: '#059669' }}>
                            <FiMail size={36} />
                        </div>
                        <h1 style={styles.title}>Você está recebendo emails da UEN</h1>
                        <p style={styles.subtitle}>
                            Enviamos avisos importantes sobre sua carteirinha
                            {email ? <> para <strong>{email}</strong></> : null}.
                            Se preferir parar, clique no botão abaixo.
                        </p>
                        <p style={styles.warn}>
                            Mesmo se cancelar, ainda enviaremos emails essenciais
                            (recuperação de senha, por exemplo).
                        </p>
                        <button
                            type="button"
                            onClick={handleToggle}
                            disabled={loading}
                            style={{ ...styles.submitAvioso, opacity: loading ? 0.65 : 1 }}
                        >
                            {loading ? (
                                <><span style={styles.spinnerSmall} /> Cancelando…</>
                            ) : (
                                <><FiBellOff size={16} /> Cancelar inscrição</>
                            )}
                        </button>
                        <Link to="/login" style={styles.backLink}>
                            <FiArrowLeft size={14} /> Voltar para o login
                        </Link>
                    </div>
                )}

                {status === 'unsubscribed' && (
                    <div style={styles.centerBox}>
                        <div style={{ ...styles.iconCircle, background: '#FEF3C7', color: '#B45309' }}>
                            <FiBellOff size={36} />
                        </div>
                        <h1 style={styles.title}>Inscrição cancelada</h1>
                        <p style={styles.subtitle}>
                            {email ? <><strong>{email}</strong> não </> : 'Você não '}
                            receberá mais notificações da UEN sobre carteirinha.
                            Mudou de ideia? Você pode voltar a receber a qualquer momento.
                        </p>
                        <button
                            type="button"
                            onClick={handleToggle}
                            disabled={loading}
                            style={{ ...styles.submit, opacity: loading ? 0.65 : 1 }}
                        >
                            {loading ? (
                                <><span style={styles.spinnerSmall} /> Reinscrevendo…</>
                            ) : (
                                <><FiBell size={16} /> Voltar a receber emails</>
                            )}
                        </button>
                        <Link to="/login" style={styles.backLink}>
                            <FiArrowLeft size={14} /> Voltar para o login
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

const styles = {
    page: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #ECFDF5 0%, #FFFFFF 60%, #F0FDFA 100%)',
        display: 'grid', placeItems: 'center',
        padding: '24px 16px',
        fontFamily: "'Inter', 'Roboto', system-ui, sans-serif",
        color: '#0F172A',
    },
    card: {
        width: '100%', maxWidth: 460, background: '#FFFFFF',
        borderRadius: 16,
        boxShadow: '0 24px 48px -16px rgba(15, 23, 42, 0.18), 0 0 0 1px rgba(15,23,42,0.04)',
        padding: '40px 36px',
    },
    brand: {
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 32,
    },
    brandLogo: {
        width: 44, height: 44, borderRadius: 12,
        background: 'linear-gradient(135deg, #059669 0%, #0D9488 100%)',
        color: '#fff', display: 'grid', placeItems: 'center',
        fontWeight: 800, fontSize: 18,
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
    },
    brandText: { display: 'flex', flexDirection: 'column', lineHeight: 1.1 },
    brandName: { fontWeight: 700, fontSize: '1.1rem', color: '#0F172A' },
    brandTag: { fontSize: '0.75rem', color: '#64748B', marginTop: 2 },

    title: {
        fontSize: '1.45rem', fontWeight: 700, color: '#0F172A',
        margin: '0 0 10px', letterSpacing: '-0.02em',
    },
    subtitle: {
        color: '#475569', margin: '0 0 14px', fontSize: '0.92rem',
        lineHeight: 1.6,
    },
    warn: {
        background: '#FFFBEB', color: '#92400E',
        borderLeft: '3px solid #F59E0B', padding: '10px 12px',
        margin: '0 0 18px', fontSize: '0.82rem', lineHeight: 1.5,
        textAlign: 'left', borderRadius: 6,
    },

    submit: {
        padding: '12px 22px',
        background: 'linear-gradient(135deg, #059669 0%, #10B981 100%)',
        color: '#fff', border: 'none', borderRadius: 10,
        fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
        boxShadow: '0 4px 12px -2px rgba(5, 150, 105, 0.4)',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, fontFamily: 'inherit',
    },
    submitAvioso: {
        padding: '12px 22px',
        background: '#FFFFFF',
        color: '#B45309', border: '1.5px solid #F59E0B', borderRadius: 10,
        fontSize: '0.95rem', fontWeight: 600, cursor: 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
        gap: 8, fontFamily: 'inherit',
    },
    spinner: {
        width: 22, height: 22, border: '2px solid rgba(5,150,105,0.2)',
        borderTopColor: '#059669', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
        margin: '0 auto 14px',
    },
    spinnerSmall: {
        width: 14, height: 14, border: '2px solid rgba(180,83,9,0.3)',
        borderTopColor: '#B45309', borderRadius: '50%',
        animation: 'spin 0.8s linear infinite',
    },
    backLink: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        marginTop: 22, color: '#059669', fontSize: '0.88rem',
        fontWeight: 600, textDecoration: 'none',
    },
    centerBox: { textAlign: 'center' },
    iconCircle: {
        width: 72, height: 72, borderRadius: '50%',
        display: 'grid', placeItems: 'center', margin: '0 auto 18px',
    },
};
